/* eslint-disable @typescript-eslint/no-misused-promises */
import {chunkRecoveryTarget} from './chunk-recovery';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { SharedService } from './services/shared.service';
import { AuthService } from './services/auth.service';
import { SubscriptionService } from './services/subscription.service';
import { distinctUntilChanged, filter, lastValueFrom } from 'rxjs';
import { SubscribeModalComponent } from './common/elements/modals/subscribe/subscribe-modal.component';
import { ProfileService } from './services/profile.service';
import { OrganizationService } from './services/organization.service';
import { MediaScanService } from './projects/media-scan/media-scan.service';
import { PosthogService } from './services/posthog.service';
import { DialogService } from 'primeng/dynamicdialog';
import { PrimeNG } from 'primeng/config';
import { environment } from 'src/environments/environment';
import { ToastV2Service } from './services/toast-v2.service';
import { MFA_SETUP_ROUTE } from './common/mfa.contract';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  providers: [DialogService],
})
export class AppComponent implements OnInit, AfterViewChecked {
  title = 'kwf';
  closeSideBar = true;

  mediaScanService = inject(MediaScanService);
  activatedRoute = inject(ActivatedRoute);

  mobileSidebarToggle = true;
  environment = environment;
  toastService = inject(ToastV2Service);

  constructor(
    public router: Router,
    readonly sharedService: SharedService,
    private readonly cdr: ChangeDetectorRef,
    readonly authService: AuthService,
    readonly subscriptionService: SubscriptionService,
    readonly profileService: ProfileService,
    private posthogService: PosthogService,
    public dialogService: DialogService,
    private primeng: PrimeNG,
    private readonly organizationService: OrganizationService
  ) {}

  ngOnInit(): void {
    this.handleStaleChunkErrors();

    this.activatedRoute.queryParams
      .pipe(distinctUntilChanged())
      .subscribe(async (params) => {
        const sessionId = params['session_id'];
        if (!sessionId || sessionId === 'canceled') return;
        try {
          await this.subscriptionService.verifyCheckoutSessionCf(sessionId);
        } catch (e) {
          console.error('Failed to verify checkout session', e);
          await this.router.navigate(['/payment-issue']);
        } finally {
          this.toastService.callSuccessToast(
            'Welcome to Apex Compliance',
            'Thank you!'
          );
        }
      });

    this.authService.$authChanged
      .pipe(distinctUntilChanged())
      .subscribe(async (email) => {
        if (!email) return;
        this.posthogService.alias(email);
        this.posthogService.init(email);
      });

    this.primeng.ripple.set(true);
  }

  ngAfterViewChecked(): void {
    this.sharedService.$sideBarDisplay.subscribe((res) => {
      this.closeSideBar = res;
      this.cdr.detectChanges();
    });
  }

  /** Refresh stale deployment assets once without losing the requested route. */
  private handleStaleChunkErrors(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationError))
      .subscribe((event) => {
        if (!this.isChunkLoadError((event as NavigationError).error)) return;

        const target = chunkRecoveryTarget((event as NavigationError).url, window.location.origin);
        const reloadKey = target.key;
        if (sessionStorage.getItem(reloadKey)) return;

        sessionStorage.setItem(reloadKey, '1');
        window.location.replace(target.href);
      });

    // Clear the retry guard once a navigation succeeds so a later deploy can reload again.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        sessionStorage.removeItem(
          chunkRecoveryTarget((event as NavigationEnd).urlAfterRedirects, window.location.origin).key
        );
      });
  }

  private isChunkLoadError(error: unknown): boolean {
    const message =
      typeof error === 'string'
        ? error
        : (error as { message?: string })?.message ?? '';
    return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError/i.test(
      message
    );
  }

  shouldShowSidebar(): boolean {
    if (!this.router.navigated) return false;
    const url = this.router.url;
    const excludedRoutes = [
      '/login',
      // The MCP connector's OAuth sign-in screen. Signed-out for most visitors, and even for
      // a signed-in one it is a single decision with somewhere else to be - a sidebar would
      // only invite the user to wander off mid-authorization.
      '/mcp-authorize',
      '/subscription',
      '/terms-and-conditions',
      '/privacy-policy',
      '/cookie-policy',
      '/user-guidelines',
      '/accept-invitation',
      '/signup-org',
      '/about',
      '/payment-issue',
      '/manage-subscription',
      // The MFA interstitial is a blocking full-screen gate: every org-scoped call 428s for
      // this user, so the sidebar could only ever render a skeleton that never resolves.
      MFA_SETUP_ROUTE,
    ];
    return !excludedRoutes.some(
      (route) => route === url || url.includes(route)
    );
  }
}
